// Settings.jsx
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'intl-tel-input/build/css/intlTelInput.css';

import intlTelInput from 'intl-tel-input';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import '../css/Settings.css';
import useIdleLogout from "../hooks/useIdleLogout";
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
const REGISTER_API = `${API_BASE_URL}/api/register`;

// List endpoint (admins + super admins)
const ADMINS_API =
  (process.env.REACT_APP_ADMIN_USERS_API_URL || '').replace(/\/+$/, '') ||
  `${API_BASE_URL}/api/admin/admins`;

// Preferred base endpoints (we'll try fallbacks automatically)
const DEACTIVATE_API = (process.env.REACT_APP_ADMIN_DEACTIVATE_API_URL || `${API_BASE_URL}/api/admin/deactivate`).replace(/\/+$/, '');
const ACTIVATE_API   = (process.env.REACT_APP_ADMIN_ACTIVATE_API_URL   || `${API_BASE_URL}/api/admin/activate`).replace(/\/+$/, '');
const DELETE_API     = (process.env.REACT_APP_ADMIN_DELETE_API_URL     || `${API_BASE_URL}/api/admin/delete`).replace(/\/+$/, '');

const FALLBACK_ADMIN_NAME = (process.env.REACT_APP_ADMIN_OPERATOR_NAME || '').trim() || 'Admin';

/* -------------------- helpers -------------------- */
function safeParse(json){ try { return JSON.parse(json); } catch { return null; } }
function b64Decode(str){ try { return atob(str.replace(/-/g,'+').replace(/_/g,'/')); } catch { return ''; } }
function readJwtPayload(token){
  if(!token || token.split('.').length<2) return null;
  const [,p] = token.split('.');
  return safeParse(b64Decode(p));
}
function normalizeRole(v){
  const raw = String(v ?? '').toLowerCase().trim();
  const compact = raw.replace(/\s+/g,'');
  if ((raw.includes('super') && raw.includes('admin')) || compact === 'superadmin') return 'super admin';
  if (raw === 'finance') return 'finance';
  return 'admin';
}

const ROLE_BADGE = {
  'super admin': { bg: '#0d6efd', label: 'Super Admin' },
  'finance':     { bg: '#198754', label: 'Finance' },
  'admin':       { bg: '#6c757d', label: 'Admin' },
};
function getCurrentAdminId(){
  const direct = Number(localStorage.getItem('user_id') || localStorage.getItem('admin_id'));
  if(!Number.isNaN(direct) && direct) return direct;
  const userObj = safeParse(localStorage.getItem('user'));
  const idFromUser = userObj?.user_id ?? userObj?.id ?? userObj?._id ?? userObj?.userId ?? userObj?.userID;
  if(idFromUser!==undefined && idFromUser!==null) return Number(idFromUser);
  const tok = localStorage.getItem('access_token') || '';
  const p = readJwtPayload(tok);
  const jId = p?.user_id ?? p?.sub ?? p?.id ?? p?.userId;
  if(jId!==undefined && jId!==null) return Number(jId);
  return NaN;
}
function getCurrentAdminName(){
  const userObj = safeParse(localStorage.getItem('user')) || {};
  const byUser = userObj.user_name ?? userObj.admin_name ?? userObj.name ?? userObj.full_name;
  if(byUser) return String(byUser);
  const tok = localStorage.getItem('access_token') || '';
  const p = readJwtPayload(tok) || {};
  const byJwt = p.name ?? p.user_name ?? p.given_name ?? p.preferred_username;
  return (byJwt && String(byJwt)) || FALLBACK_ADMIN_NAME;
}
function isSuperAdmin(viewerId){
  const userObj = safeParse(localStorage.getItem('user')) || {};
  const roleStr = normalizeRole(userObj.role || userObj.user_role);
  const flags   = !!(userObj.is_super || userObj.is_super_admin || userObj.super || userObj.isOwner);
  const tok = localStorage.getItem('access_token') || '';
  const p = readJwtPayload(tok) || {};
  const jwtRoleStr = normalizeRole(p.role || p.roles);
  const envIds = (process.env.REACT_APP_SUPER_ADMIN_IDS || '')
    .split(',').map(s => Number(s.trim())).filter(n => !Number.isNaN(n));
  const envSaysSuper = envIds.length>0 && !Number.isNaN(viewerId) && envIds.includes(Number(viewerId));
  return roleStr === 'super admin' || jwtRoleStr === 'super admin' || flags || envSaysSuper;
}
function waitForUtils(maxMs=2500, interval=60){
  return new Promise((resolve)=>{
    const start = Date.now();
    const tick = ()=>{
      if(window.intlTelInputUtils) return resolve(true);
      if(Date.now()-start>=maxMs) return resolve(false);
      setTimeout(tick, interval);
    };
    tick();
  });
}

/* -------------------- component -------------------- */
const ONLY_COUNTRIES = ['bt','in','np','bd','pk','lk','th','my','sg','id','ph','vn','cn','jp','kr','mm'];

/**
 * Per-country rules for *national* digits length
 * (digits AFTER the country calling code, ignoring + and spaces).
 *
 * These are used even if utils.js fails to load, so validation
 * still works offline / on slow networks.
 */
const PHONE_RULES = {
  bt: { min: 8, max: 8 },    // Bhutan: 8-digit NSN after +975
  in: { min: 10, max: 10 },  // India: 10-digit NSN after +91
  np: { min: 10, max: 10 },  // Nepal: 10-digit NSN after +977
  bd: { min: 10, max: 10 },  // Bangladesh: typical 10-digit NSN after +880
  pk: { min: 10, max: 10 },  // Pakistan: 10-digit NSN after +92
  lk: { min: 9, max: 9 },    // Sri Lanka: 9-digit NSN after +94
  th: { min: 9, max: 10 },   // Thailand: 9–10 digit NSN after +66
  my: { min: 8, max: 9 },    // Malaysia: 8–9 digit NSN after +60
  sg: { min: 8, max: 8 },    // Singapore: 8-digit NSN after +65
  id: { min: 8, max: 12 },   // Indonesia: 8–12 digit NSN after +62
  ph: { min: 10, max: 10 },  // Philippines: 10-digit NSN after +63 (mobile)
  vn: { min: 9, max: 10 },   // Vietnam: 9–10 digit NSN after +84
  cn: { min: 11, max: 11 },  // China: 11-digit NSN after +86 (mobile)
  jp: { min: 10, max: 10 },  // Japan: 10-digit NSN after +81 (mobile)
  kr: { min: 9, max: 10 },   // South Korea: 9–10 digit NSN after +82
  mm: { min: 7, max: 10 },   // Myanmar: 7–10 digit NSN after +95
};

export default function Settings(){
    useIdleLogout();
  const [fullName,setFullName]=useState('');
  const [email,setEmail]=useState('');
  const [role,setRole]=useState('admin');
  const [tempPassword,setTempPassword]=useState('');
  const [pwdVisible,setPwdVisible]=useState(false);
  const [submitting,setSubmitting]=useState(false);

  const [admins,setAdmins]=useState([]);
  const [adminsLoading,setAdminsLoading]=useState(false);
  const [adminsError,setAdminsError]=useState('');
  const [rowBusy,setRowBusy]=useState({});

  const currentAdminId   = getCurrentAdminId();
  const currentAdminName = getCurrentAdminName();
  const canOperate       = isSuperAdmin(currentAdminId); // only super admin sees action buttons

  const phoneRef = useRef(null);
  const itiRef = useRef(null);
  const initedRef = useRef(false); 

  // INIT phone input
  useLayoutEffect(()=>{
    if(!phoneRef.current || initedRef.current) return;
    initedRef.current = true;

    itiRef.current = intlTelInput(phoneRef.current,{
      initialCountry:'bt',
      onlyCountries:ONLY_COUNTRIES,
      nationalMode:false,
      autoPlaceholder:'polite',
      formatOnDisplay:true,
      allowDropdown:true,
      utilsScript:'https://cdn.jsdelivr.net/npm/intl-tel-input@18.5.6/build/js/utils.js',
    });

    return ()=>{ try{ itiRef.current?.destroy(); }catch{} itiRef.current=null; initedRef.current=false; };
  },[]);

  const fetchAdmins = async ()=>{
    try{
      setAdminsLoading(true);
      setAdminsError('');
      const token = localStorage.getItem('access_token') || '';
      const res = await fetch(ADMINS_API,{
        headers:{ Accept:'application/json', ...(token?{Authorization:`Bearer ${token}`}:{}) },
      });
      if(!res.ok){
        let msg = `Failed to load admins (${res.status})`;
        try{
          const txt = await res.text();
          const j = JSON.parse(txt);
          msg = j.error || j.message || txt || msg;
        }catch{}
        throw new Error(msg);
      }
      const data = await res.json();
      const items = Array.isArray(data)?data:(data?.data??[]);
      setAdmins(items);
    }catch(err){
      setAdminsError(err.message || 'Failed to load admins.');
      setAdmins([]);
    }finally{
      setAdminsLoading(false);
    }
  };

  useEffect(()=>{ fetchAdmins(); /* eslint-disable-next-line */ },[]);

  const generateStrongPassword=()=>{
    const chars='ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*()_+-=';
    let pwd=''; for(let i=0;i<12;i++) pwd+=chars[Math.floor(Math.random()*chars.length)];
    setTempPassword(pwd);
  };

  async function buildPhoneFacts(iti){
    await waitForUtils();
    const raw=(phoneRef.current?.value||'').trim();
    const hasPlus = raw.startsWith('+');
    const sel = iti.getSelectedCountryData()||{};
    const dial = String(sel?.dialCode||'');
    const libE164 = (iti.getNumber()||'').trim();
    const digitsOnly = (libE164||raw).replace(/\D/g,'');
    let nationalDigits = raw.replace(/\D/g,'');
    try{
      if(window.intlTelInputUtils){
        const nat = iti.getNumber(window.intlTelInputUtils.numberFormat.NATIONAL)||'';
        nationalDigits = nat.replace(/\D/g,'')||nationalDigits;
      }
    }catch{}
    const e164 = hasPlus ? (libE164 || `+${digitsOnly}`) : (dial ? `+${dial}${nationalDigits}` : libE164);
    return { e164 };
  }

  async function validateNewAdmin(){
    if(!fullName.trim()) return 'Please enter full name.';
    const re=/^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if(!re.test(email)) return 'Please enter a valid email.';
    if(!tempPassword) return 'Please set a temporary password.';
    const strong=/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if(!strong.test(tempPassword)) return 'Password must be 8+ chars with uppercase, lowercase, number and special.';
    if(!itiRef.current) return 'Phone field not ready—please try again.';

    // ---- PHONE VALIDATION (per-country length, excluding country code) ----
    const iti = itiRef.current;
    const raw = (phoneRef.current?.value || '').trim();
    if (!raw) return 'Please enter phone number.';

    const countryData = iti.getSelectedCountryData() || {};
    const iso2 = (countryData.iso2 || '').toLowerCase();
    const countryName = countryData.name || 'this country';
    const dialCode = String(countryData.dialCode || '');

    // Digits entered (no spaces, +, etc.)
    const digits = raw.replace(/\D/g, '');

    // Compute NATIONAL digits: strip country code if it appears at the start
    let nationalDigits = digits;
    if (dialCode && digits.startsWith(dialCode)) {
      nationalDigits = digits.slice(dialCode.length);
    }

    // Apply our static length rules (works even if utils.js fails)
    const rule = PHONE_RULES[iso2];
    if (rule) {
      const len = nationalDigits.length;
      if (len < rule.min || len > rule.max) {
        const expected =
          rule.min === rule.max
            ? `${rule.min} digits`
            : `${rule.min}–${rule.max} digits`;
        return `Phone number must have ${expected} (excluding country code) for ${countryName}. You entered ${len}.`;
      }
    } else {
      // Generic sanity check if we somehow don't have rules for this country
      if (nationalDigits.length < 6) {
        return `Please enter a valid phone number for ${countryName}.`;
      }
    }

    // If utils are available, also let libphonenumber validate structure
    const hasUtils = await waitForUtils();
    if (hasUtils && window.intlTelInputUtils && typeof iti.isValidNumber === 'function') {
      const isValid = iti.isValidNumber();
      if (!isValid) {
        let msg = `Please enter a valid phone number for ${countryName}.`;
        try {
          const errCode = iti.getValidationError();
          const ve = window.intlTelInputUtils.validationError || {};
          if (errCode === ve.TOO_SHORT) {
            msg = `Phone number is too short for ${countryName}.`;
          } else if (errCode === ve.TOO_LONG) {
            msg = `Phone number is too long for ${countryName}.`;
          } else if (errCode === ve.NOT_A_NUMBER) {
            msg = 'Phone number must contain digits only.';
          }
        } catch {}
        return msg;
      }
    }
    // -----------------------------------------------------------------------

    return '';
  }

  const handleCreateAdmin = async (e)=>{
    e.preventDefault();
    const err = await validateNewAdmin();
    if(err) return Swal.fire('Check form', err, 'warning');

    const { e164 } = await buildPhoneFacts(itiRef.current);

    const payload = {
      user: {
        user_name: fullName.trim(),
        email,
        phone: e164,
        password: tempPassword,
        role: role,
      },
    };

    try{
      setSubmitting(true);
      const token = localStorage.getItem('access_token') || '';
      const res = await fetch(REGISTER_API,{
        method:'POST',
        headers:{ 'Content-Type':'application/json', ...(token?{Authorization:`Bearer ${token}`}:{}) },
        body: JSON.stringify(payload),
      });
      if(!res.ok){
        let msg='Could not create admin.';
        try{ const txt=await res.text(); const j=JSON.parse(txt); msg=j.error||j.message||txt||msg; }catch{}
        throw new Error(msg);
      }

      await Swal.fire({
        icon:'success',
        title:'Admin Created',
        html:`<div class="text-start">
          <div><strong>Name:</strong> ${fullName}</div>
          <div><strong>Email:</strong> ${email}</div>
          <div><strong>Temp Password:</strong> ${tempPassword}</div>
          <div><strong>Phone:</strong> ${e164}</div>
        </div>`,
      });

      setFullName(''); setEmail(''); setRole('admin'); setTempPassword(''); setPwdVisible(false);
      if(phoneRef.current){ phoneRef.current.value=''; try{ itiRef.current?.setCountry('bt'); }catch{} }
      fetchAdmins();
    }catch(er){
      Swal.fire('Error', er.message||'Could not create admin.', 'error');
    }finally{ setSubmitting(false); }
  };

  const safe = (v,d='') => (v===null||v===undefined?d:String(v));
  const pickName  = (u)=> safe(u?.user_name ?? u?.name ?? u?.full_name ?? '—');
  const pickRole  = (u)=> normalizeRole(u?.role ?? u?.user_role ?? 'admin');
  const pickEmail = (u)=> safe(u?.email,'—');
  const pickPhone = (u)=> safe(u?.phone ?? u?.mobile ?? u?.contact,'—');
  const rowId = (u)=> u?.user_id ?? u?.id ?? u?._id ?? u?.userId ?? u?.userID ?? u?.uid;
  const isActive = (u)=>{
    if(typeof u?.active==='boolean') return u.active;
    if(typeof u?.is_active==='boolean') return u.is_active;
    if(typeof u?.disabled==='boolean') return !u.disabled;
    if(typeof u?.status==='string') return u.status.toLowerCase()==='active';
    if(typeof u?.active==='number') return u.active===1;
    if(typeof u?.is_active==='number') return u.is_active===1;
    return true;
  };
  const setBusy=(id,val)=> setRowBusy(s=>({...s,[id]:val}));

  // Smarter action helper: replace placeholders and try common shapes
  const postActionSmart = async (baseUrl, targetId, actionWord) => {
    const token = localStorage.getItem('access_token') || '';
    const actorId = Number(currentAdminId);
    const actorName = currentAdminName;
    const id = encodeURIComponent(targetId);

    // normalize/replace placeholders
    let base = String(baseUrl || '').replace(/\/+$/,'');
    if (/:user_id\b|:admin_id\b|:id\b/.test(base)) {
      base = base.replace(/:user_id\b/g, id).replace(/:admin_id\b/g, id).replace(/:id\b/g, id);
    }

    const api = API_BASE_URL.replace(/\/+$/,'');

    const baseBody = {
      user_id: actorId, admin_user_id: actorId, operator_id: actorId,
      admin_name: actorName, operator_name: actorName,
      target_user_id: Number(targetId), target_admin_id: Number(targetId),
      admin_id: Number(targetId), id: Number(targetId),
    };

    const candidates = [
      { m:'POST',   u:`${base}`,                b:{...baseBody} },
      { m:'POST',   u:`${base}/user_id/${id}`,  b:{...baseBody} },
      { m:'POST',   u:`${base}/admin_id/${id}`, b:{...baseBody} },
      { m:'POST',   u:`${base}/${id}`,          b:{...baseBody} },
      { m:'DELETE', u:`${base}/user_id/${id}`,  b:{...baseBody} },
      { m:'DELETE', u:`${base}/${id}`,          b:{...baseBody} },
      { m:'POST',   u:`${api}/api/admin/${id}/${actionWord}`, b:{...baseBody} },
      { m:'POST',   u:`${api}/api/admin/users/${id}/${actionWord}`, b:{...baseBody} },
      { m:'DELETE', u:`${api}/api/admin/users/${id}`, b:{...baseBody} },
      { m:'POST',   u:`${api}/api/admin/${actionWord}_user/${id}`, b:{...baseBody} },
      { m:'POST',   u:`${api}/api/admin/${actionWord}`, b:{...baseBody} },
    ];

    let lastErr = null;
    for (const c of candidates) {
      try {
        const res = await fetch(c.u, {
          method: c.m,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: c.m === 'GET' ? undefined : JSON.stringify(c.b),
        });
        if (res.ok) { try { return await res.json(); } catch { return {}; } }
        const txt = await res.text().catch(()=> '');
        lastErr = new Error(txt || `Failed to ${actionWord} (${res.status}) [${c.m} ${c.u}]`);
      } catch (e) { lastErr = e; }
    }
    throw lastErr || new Error(`Failed to ${actionWord}. No endpoint matched.`);
  };

  const deleteAdmin = async (u)=>{
    const targetId = rowId(u);
    if(!targetId) return Swal.fire('Error','Missing user id.','error');
    if(!canOperate) return Swal.fire('Forbidden','Only super admin can perform this.','info');
    if(!Number.isNaN(currentAdminId) && Number(targetId)===Number(currentAdminId)){
      return Swal.fire('Oops','You cannot delete your own account.','info');
    }
    const ok = await Swal.fire({
      icon:'warning',
      title:'Delete admin?',
      text:`This will permanently remove ${pickName(u)}.`,
      showCancelButton:true,
      confirmButtonText:'Delete',
      confirmButtonColor:'#6c757d'
    });
    if(!ok.isConfirmed) return;
    try{
      setBusy(targetId,true);
      await postActionSmart(DELETE_API, targetId, 'delete');
      setAdmins(list=>list.filter(x=>rowId(x)!==targetId));
      Swal.fire('Deleted','Admin removed.','success');
    }catch(err){
      Swal.fire('Error', err.message||'Failed to delete.','error');
    }finally{ setBusy(targetId,false); }
  };

  const toggleAdminActive = async (u)=>{
    const targetId = rowId(u);
    if(!targetId) return Swal.fire('Error','Missing user id.','error');
    if(!canOperate) return Swal.fire('Forbidden','Only super admin can perform this.','info');
    if(!Number.isNaN(currentAdminId) && Number(targetId)===Number(currentAdminId)){
      return Swal.fire('Oops','You cannot change your own active status.','info');
    }
    const activeNow = isActive(u);
    const base = activeNow ? DEACTIVATE_API : ACTIVATE_API;
    const verb = activeNow ? 'deactivate' : 'activate';
    try{
      setBusy(targetId,true);
      await postActionSmart(base, targetId, verb);
      setAdmins(list=>list.map(x=>{
        const xid=rowId(x); if(xid!==targetId) return x;
        const next={...x};
        if('active' in next) next.active = !activeNow;
        else if('is_active' in next) next.is_active = !activeNow;
        else if('disabled' in next) next.disabled = activeNow;
        else next.status = !activeNow ? 'active' : 'inactive';
        return next;
      }));
      Swal.fire('Updated', `Admin ${activeNow ? 'deactivated' : 'activated'}.`, 'success');

    }catch(err){
      Swal.fire('Error', err.message||'Failed to update status.','error');
    }finally{ setBusy(targetId,false); }
  };

  const visibleAdmins = admins.filter(u=>{
    const id=rowId(u);
    if(id===undefined||id===null) return true;
    if(Number.isNaN(currentAdminId)) return true;
    return Number(id)!==Number(currentAdminId);
  });

  return (
    <div className="settings-page">
      <div className="settings-stack">
        {/* Add New Admin */}
        <div className="card settings-card">
          <div className="card-gradient-header">
            <h5 className="card-gradient-title">Add New Admin</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleCreateAdmin} className="form-vertical">
              <div className="row two-col">
                <div className="col">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-control pill"
                    placeholder="Enter Complete Name"
                    value={fullName}
                    onChange={e=>setFullName(e.target.value)}
                  />
                </div>
                <div className="col">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control pill"
                    placeholder="Email Address"
                    value={email}
                    onChange={e=>setEmail(e.target.value)}
                  />
                </div>
              </div>

              <label className="form-label mt-3">Role</label>
              <select
                className="form-select pill"
                value={role}
                onChange={e=>setRole(e.target.value)}
              >
                <option value="admin">Admin</option>
                <option value="finance">Finance</option>
              </select>

              <label className="form-label mt-3">Phone</label>
              <div className="password-wrapper">
                <i className="bi bi-telephone-fill input-icon" />
                <input
                  ref={phoneRef}
                  type="tel"
                  id="adminPhone"
                  className="form-control pill form-control--phone"
                  placeholder="Phone Number"
                  autoComplete="tel"
                  inputMode="tel"
                  aria-label="Admin phone number"
                />
              </div>

              <label className="form-label mt-3">Temporary Password</label>
              <div className="row align-mid">
                <div className="col grow">
                  <div className="password-wrapper">
                    <input
                      type={pwdVisible?'text':'password'}
                      className="form-control pill"
                      placeholder="Set a temporary password"
                      value={tempPassword}
                      onChange={e=>setTempPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="icon-inset"
                      onClick={()=>setPwdVisible(s=>!s)}
                      aria-label={pwdVisible?'Hide password':'Show password'}
                    >
                      <i className={`bi ${pwdVisible?'bi-eye-slash':'bi-eye'}`} />
                    </button>
                  </div>
                </div>
                <div className="col shrink">
                  <button type="button" className="btn soft-primary pill" onClick={generateStrongPassword}>
                    <i className="bi bi-stars me-2" /> Generate
                  </button>
                </div>
              </div>

              <small className="text-muted d-block mt-2">The admin can change this password after first login.</small>

              <button className="btn primary-gradient pill mt-4" disabled={submitting}>
                {submitting?'Creating…':'Create Admin'}
              </button>
            </form>
          </div>
        </div>

        {/* Admin & Super Admin Users Table (self excluded) */}
        <div className="card settings-card">
          <div className="card-gradient-header">
            <h5 className="card-gradient-title m-0">Administrators</h5>
          </div>

          <div className="card-body">
            {adminsError ? (
              <div className="alert alert-warning">{adminsError}</div>
            ) : (
              <div className="table-responsive admin-table-wrap">
                <table className="table admin-table">
                  <thead>
                    <tr>
                      <th style={{width:'72px'}}>SI.No</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th style={{width:'160px'}}>Role</th>
                      {canOperate && <th style={{width:'172px'}}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {adminsLoading ? (
                      <tr><td colSpan={canOperate?6:5}>Loading…</td></tr>
                    ) : visibleAdmins.length===0 ? (
                      <tr><td colSpan={canOperate?6:5}>No admins found.</td></tr>
                    ) : (
                      visibleAdmins.map((u,idx)=>{
                        const id=rowId(u);
                        const active=isActive(u);
                        const busy=!!rowBusy[id];
                        const roleLabel = pickRole(u);

                        return (
                          <tr key={id ?? idx}>
                            <td data-label="SI.No">{idx+1}</td>
                            <td data-label="Name">{pickName(u)}</td>
                            <td data-label="Email">{pickEmail(u)}</td>
                            <td data-label="Phone">{pickPhone(u)}</td>
                            <td data-label="Role">
                              <span className="badge" style={{ background: ROLE_BADGE[roleLabel]?.bg || '#6c757d', color: '#fff' }}>
                                {ROLE_BADGE[roleLabel]?.label || roleLabel}
                              </span>
                            </td>

                            {canOperate && (
                              <td data-label="Actions">
                                <div className="actions-row">
                                  {/* Activate / Deactivate */}
                                  <button
                                    type="button"
                                    className={`btn-action ${active?'btn-deactivate':'btn-activate'}`}
                                    onClick={()=>toggleAdminActive(u)}
                                    disabled={busy}
                                    title={active?'Deactivate':'Activate'}
                                    aria-label={active?'Deactivate':'Activate'}
                                  >
                                    <i className={`bi ${active ? 'bi-person-dash-fill' : 'bi-person-check-fill'}`} />
                                  </button>

                                  {/* Delete */}
                                  <button
                                    type="button"
                                    className="btn-action btn-delete"
                                    onClick={()=>deleteAdmin(u)}
                                    disabled={busy}
                                    title="Delete"
                                    aria-label="Delete"
                                  >
                                    <i className="bi bi-trash" />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <footer className="dashboard-footer">
          <small>© {new Date().getFullYear()}  <strong>སྟབས་བདེ། </strong> Admin • All rights reserved</small>
        </footer>
      </div>
    </div>
  );
}
